import React, { useState, useRef, useEffect } from 'react';
import { UserCircle, User, LogOut } from 'lucide-react';

export interface UserMenuProps {
  username?: string;
  email?: string;
  onSettingsClick?: () => void;
  onLogoutClick?: () => void;
  darkModeToggle?: React.ReactNode;
  className?: string;
  /** Translation function — defaults to identity (returns key as-is) */
  t?: (key: string) => string;
}

const defaultT = (key: string) => key;

export const UserMenu: React.FC<UserMenuProps> = ({
  username,
  email,
  onSettingsClick,
  onLogoutClick,
  className = '',
  t = defaultT,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} ref={menuRef} data-id="user-menu">
      {/* Menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        aria-label="User menu"
      >
        <UserCircle className="w-6 h-6 text-foreground-secondary" />
        {username && (
          <span className="hidden md:block text-sm font-medium text-foreground-secondary">
            {username}
          </span>
        )}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
          {/* User info */}
          {(username || email) && (
            <>
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                {username && (
                  <p className="text-sm font-semibold text-foreground">
                    {username}
                  </p>
                )}
                {email && (
                  <p className="text-xs text-muted-foreground truncate">
                    {email}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Menu items */}
          {onSettingsClick && (
            <button
              onClick={() => {
                onSettingsClick();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground-secondary hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <User className="w-5 h-5" />
              {t('Profile')}
            </button>
          )}

          {/* Dark mode toggle */}
          {/* {darkModeToggle && (
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
              {darkModeToggle}
            </div>
          )} */}

          {/* Logout */}
          {onLogoutClick && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              <button
                onClick={() => {
                  onLogoutClick();
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                {t('Sign Out')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
